import React, { useEffect, useState } from "react";
import {
  CalculateCostsResponseSchema,
  EvaluatedPricingLine,
} from "@forgepoint/shared";
import { fetchApi } from "@/lib/api";
import { useItemVersionMs } from "./itemsStore";
import { PriceRow, PriceRowCell } from "./PriceRow";
import { PricingLineRows } from "./PricingLineRows";
import { sumMargins } from "./margins";
import { quoteCurrencyUnitPrice } from "./quoteCurrencyPrice";
import {
  quoteCurrencyRate,
  type QuoteCurrencyFields,
} from "@/lib/quoteCurrency";

interface UnitPriceTableProps {
  itemId: string;
  quantities: string[];
  scrollRef?: (el: HTMLDivElement | null) => void;
  onScrollSync?: React.UIEventHandler<HTMLDivElement>;
  /** Changes whenever a save inside the BOM table should refetch these prices. */
  refreshKey?: number;
  /** The quote's currency fields; a multi-currency quote adds a quote-currency price row. */
  currencyContext?: QuoteCurrencyFields;
}

interface PriceData {
  quantity: string;
  unitCost: string;
  marginAmount: string;
  unitPrice: string;
  pricingLines?: EvaluatedPricingLine[];
}

/**
 * Renders the per-quantity unit-price waterfall for an item: base cost →
 * COST-phase pricing lines → margin → PRICE-phase pricing lines → total price.
 * Each step shows the signed amount it adds, and the steps reconcile exactly to
 * the total price. A multi-currency quote adds its converted total price and rate.
 */
export function UnitPriceTable({
  itemId,
  quantities,
  scrollRef,
  onScrollSync,
  refreshKey,
  currencyContext,
}: UnitPriceTableProps) {
  const [priceData, setPriceData] = useState<PriceData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const itemVersionMs = useItemVersionMs(itemId);

  useEffect(() => {
    // A superseded run must not publish: two version bumps can overlap and
    // land out of order.
    let cancelled = false;
    const fetchPrices = async () => {
      if (!itemId) {
        return;
      }

      setIsLoading(true);
      try {
        const response = await fetchApi(
          `items/${itemId}/calculate-costs`,
          "POST",
          { body: { quantities } },
        );

        if (cancelled) {
          return;
        }
        if (response.ok && response.data) {
          const { costs } = CalculateCostsResponseSchema.parse(response.data);

          setPriceData(
            costs.map((cost) => ({
              quantity: cost.quantity,
              unitCost: cost.unitCost,
              marginAmount: sumMargins(cost.marginsByType),
              unitPrice: cost.unitPrice,
              pricingLines: cost.pricingLines,
            })),
          );
        }
      } catch {
        if (!cancelled) {
          setPriceData([]);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrices();
    return () => {
      cancelled = true;
    };
  }, [itemId, quantities, itemVersionMs, refreshKey]);

  const dataByQuantity = new Map(
    priceData.map((data) => [data.quantity, data]),
  );
  const cellsFor = (pick: (data: PriceData) => string): PriceRowCell[] =>
    quantities.map((quantity) => {
      const data = dataByQuantity.get(quantity);
      return { quantity, value: data ? pick(data) : null };
    });
  const quoteRate = quoteCurrencyRate(currencyContext);

  return (
    <div className="pb-4">
      <div
        ref={scrollRef}
        onScroll={onScrollSync}
        className="bg-card rounded-lg border shadow-sm overflow-x-auto"
      >
        <table
          className="w-full border-separate border-spacing-0 table-fixed"
          style={{ minWidth: `${300 + quantities.length * 120}px` }}
        >
          <thead className="bg-muted">
            <tr>
              <th className="text-left py-2 px-4 text-sm font-semibold w-[300px] min-w-[300px] sticky left-0 z-20 bg-muted border-r border-border border-b border-b-border">
                Pricing
              </th>
              {quantities.map((qty) => (
                <th
                  key={qty}
                  className="text-right py-2 px-4 text-sm font-semibold min-w-[100px] border-l border-border border-b border-b-border"
                >
                  Qty {qty}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <PriceRow
              label="Total Cost"
              isLoading={isLoading}
              cells={cellsFor((data) => data.unitCost)}
            />

            <PricingLineRows
              priceData={priceData}
              quantities={quantities}
              isLoading={isLoading}
              phase="COST"
            />

            <PriceRow
              label="Total Margin"
              signed
              isLoading={isLoading}
              cells={cellsFor((data) => data.marginAmount)}
            />

            <PricingLineRows
              priceData={priceData}
              quantities={quantities}
              isLoading={isLoading}
              phase="PRICE"
            />

            <PriceRow
              label="Total Price"
              variant="total"
              isLoading={isLoading}
              cells={cellsFor((data) => data.unitPrice)}
            />

            {currencyContext && quoteRate && (
              <PriceRow
                label={`Total Price (${quoteRate.quoteCurrency})`}
                variant="total"
                isLoading={isLoading}
                cells={cellsFor((data) =>
                  quoteCurrencyUnitPrice(data.unitPrice, currencyContext),
                )}
                currency={quoteRate.quoteCurrency}
              />
            )}
          </tbody>
        </table>
      </div>
      {quoteRate && (
        <div className="flex items-center gap-1.5 pt-2 text-xs text-muted-foreground tabular-nums">
          <span>1 {quoteRate.baseCurrency}</span>
          <span className="text-muted-foreground/50" aria-hidden>
            &rarr;
          </span>
          <span className="font-medium">
            {quoteRate.rate} {quoteRate.quoteCurrency}
          </span>
          <span>· quote exchange rate</span>
        </div>
      )}
    </div>
  );
}
